import axios from "axios";
import { buildApiUrl, formatApiUnreachableMessage, getApiBaseUrl } from "../config/api";
import { authSuccess } from "../redux/userRelated/userSlice";

export const REGISTRATION_CHECKOUT_KEY = "schoolRegistrationCheckout";

export function saveRegistrationCheckout(data) {
    try {
        sessionStorage.setItem(REGISTRATION_CHECKOUT_KEY, JSON.stringify(data));
    } catch {
        /* ignore quota / private mode */
    }
}

export function loadRegistrationCheckout() {
    try {
        const raw = sessionStorage.getItem(REGISTRATION_CHECKOUT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function clearRegistrationCheckout() {
    try {
        sessionStorage.removeItem(REGISTRATION_CHECKOUT_KEY);
    } catch {
        /* ignore */
    }
}

function registrationErrorMessage(error) {
    const baseUrl = getApiBaseUrl();
    const data = error?.response?.data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (data?.message) return String(data.message);
    if (data?.error) return String(data.error);
    if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
        return formatApiUnreachableMessage(baseUrl);
    }
    return error?.message || "Registration failed";
}

function isAvailabilityMessage(message) {
    const text = String(message || "").toLowerCase();
    return text.includes("email already exists") || text.includes("school name already exists");
}

/** Check email / school name are free — does not create an account. */
export async function validateAdminRegistration(fields) {
    const payload = {
        ...fields,
        role: "Admin",
        intent: "validate-registration",
    };

    try {
        const { data } = await axios.post(buildApiUrl("/AdminReg"), payload, {
            headers: { "Content-Type": "application/json" },
        });

        if (data?.ok === true) return true;

        if (data?.message) {
            const err = new Error(String(data.message));
            err.isAvailabilityError = isAvailabilityMessage(data.message);
            throw err;
        }

        if (data?.role === "Admin" && (data?._id || data?.id)) {
            const err = new Error(
                "Registration could not be verified right now. Please try again later or contact support.",
            );
            err.isAvailabilityError = false;
            throw err;
        }

        return true;
    } catch (error) {
        if (error?.isAvailabilityError !== undefined) throw error;
        const err = new Error(registrationErrorMessage(error));
        err.isAvailabilityError = isAvailabilityMessage(err.message);
        throw err;
    }
}

/** Create the school director account (trial or before payment). */
export async function createAdminAccount(fields, dispatch, { persistSession = true } = {}) {
    const payload = { ...fields, role: "Admin" };
    delete payload.intent;

    try {
        const { data } = await axios.post(buildApiUrl("/AdminReg"), payload, {
            headers: { "Content-Type": "application/json" },
        });

        if (data?.message && !data?.role && !(data?._id || data?.id)) {
            const err = new Error(String(data.message));
            err.isAvailabilityError = isAvailabilityMessage(data.message);
            throw err;
        }

        if (!data?.schoolName && data?.role !== "Admin") {
            throw new Error(data?.message || "Could not create school account");
        }

        if (persistSession) {
            dispatch(authSuccess(data));
        }
        return data;
    } catch (error) {
        if (error?.isAvailabilityError !== undefined) throw error;
        const err = new Error(registrationErrorMessage(error));
        err.isAvailabilityError = isAvailabilityMessage(err.message);
        throw err;
    }
}

export { isAvailabilityMessage };
